import axios from 'axios';
import {
  extract_error_message,
  parse_response_data,
} from '@/utils/api_response_helpers';
import {
  GET_ONBOARDING_FORMS_URL,
  GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
  GET_TEMPLATE_FORMS_URL,
  LINK_TEMPLATE_FORM_TO_ONBOARDING_FORM_URL,
  UNLINK_TEMPLATE_FORM_FROM_ONBOARDING_FORM_URL,
  get_request_config,
} from '@/utils/system_constants/constant';
import {
  normalize_assigned_onboarding_form,
  normalize_onboarding_form,
  normalize_template_form,
} from './template_form_management_helpers';

const template_form_management_assignment_methods = {
  load_forms_tab_data() {
    this.get_template_forms();
    this.load_onboarding_forms();
  },

  async get_template_forms() {
    this.is_loading = true;

    try {
      const response = await axios.get(
        GET_TEMPLATE_FORMS_URL,
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      this.template_forms = (parsed?.data || [])
        .map(normalize_template_form)
        .filter(Boolean);
    } catch (error) {
      this.template_forms = [];
      this.show_error(
        extract_error_message(error, 'Failed to load template forms.'),
      );
    } finally {
      this.is_loading = false;
    }
  },

  async load_onboarding_forms() {
    if (this.is_loading_onboarding_forms) return;
    this.is_loading_onboarding_forms = true;

    try {
      const response = await axios.get(
        GET_ONBOARDING_FORMS_URL,
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      this.onboarding_forms = (parsed?.data || [])
        .map(normalize_onboarding_form)
        .filter(Boolean);
    } catch (error) {
      this.onboarding_forms = [];
      this.show_error(
        extract_error_message(error, 'Failed to load onboarding forms.'),
      );
    } finally {
      this.is_loading_onboarding_forms = false;
    }
  },

  merge_assigned_onboarding_forms(items) {
    const existing_forms = Array.isArray(this.onboarding_forms) ? this.onboarding_forms : [];
    const merged_forms = [...existing_forms];
    const existing_ids = new Set(
      existing_forms.map((form) => String(form?.id || '').trim()).filter(Boolean),
    );

    items.forEach((item) => {
      const normalized_form = normalize_assigned_onboarding_form(item);
      if (!normalized_form || existing_ids.has(normalized_form.id)) return;

      existing_ids.add(normalized_form.id);
      merged_forms.push(normalized_form);
    });

    this.onboarding_forms = merged_forms;
  },

  is_onboarding_form_assigned(onboarding_form_id) {
    return Boolean(this.linked_onboarding_form_ids[String(onboarding_form_id)]);
  },

  async load_template_form_assignments(template_form_id) {
    if (!template_form_id) {
      this.linked_onboarding_form_ids = {};
      return;
    }

    this.is_loading_assignments = true;

    try {
      const response = await axios.get(
        GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
        get_request_config({
          params: { template_form_id },
        }),
      );
      const parsed = parse_response_data(response);
      const assignment_items = Array.isArray(parsed?.data) ? parsed.data : [];
      const linked_onboarding_form_ids = {};

      assignment_items
        .filter((item) => item?.is_active !== false)
        .forEach((item) => {
          const onboarding_form_id = String(item?.onboarding_form_id || '').trim();
          if (onboarding_form_id) {
            linked_onboarding_form_ids[onboarding_form_id] = true;
          }
        });

      this.merge_assigned_onboarding_forms(assignment_items);
      this.linked_onboarding_form_ids = linked_onboarding_form_ids;
    } catch (error) {
      this.linked_onboarding_form_ids = {};
      this.show_error(
        extract_error_message(error, 'Failed to load template form assignments.'),
      );
    } finally {
      this.is_loading_assignments = false;
    }
  },

  async open_assign_modal(form) {
    this.selected_assign_template_form = form || null;
    this.linked_onboarding_form_ids = {};
    this.assignment_target_onboarding_form_id = '';

    if (!this.onboarding_forms.length) {
      await this.load_onboarding_forms();
    }

    await this.load_template_form_assignments(
      this.selected_assign_template_form?.template_form_id,
    );
  },

  async submit_onboarding_form_assignment(selected_onboarding_form_id) {
    const template_form_id = Number(this.selected_assign_template_form_id || 0) || null;
    const onboarding_form_id = Number(selected_onboarding_form_id || 0) || null;

    if (!template_form_id || !onboarding_form_id) {
      this.show_warning('Assignment', 'Please select an onboarding form.');
      return;
    }

    if (this.is_submitting_assignment) return;

    this.is_submitting_assignment = true;
    this.assignment_target_onboarding_form_id = String(onboarding_form_id);
    const is_unassigning = this.is_onboarding_form_assigned(onboarding_form_id);
    const request_url = is_unassigning
      ? UNLINK_TEMPLATE_FORM_FROM_ONBOARDING_FORM_URL
      : LINK_TEMPLATE_FORM_TO_ONBOARDING_FORM_URL;
    const loader = await this.show_loader(
      is_unassigning ? 'Unassigning onboarding form...' : 'Assigning onboarding form...',
    );

    try {
      const response = await axios.post(
        request_url,
        {
          onboarding_form_id,
          template_form_id,
        },
        get_request_config(),
      );

      const parsed = parse_response_data(response);

      if (parsed?.status !== 'success') {
        this.show_error(parsed?.message || 'Failed to update onboarding form assignment.');
        return;
      }

      this.linked_onboarding_form_ids = {
        ...this.linked_onboarding_form_ids,
        [String(onboarding_form_id)]: !is_unassigning,
      };

      if (is_unassigning) {
        delete this.linked_onboarding_form_ids[String(onboarding_form_id)];
        this.linked_onboarding_form_ids = { ...this.linked_onboarding_form_ids };
      }

      this.show_success(
        parsed?.message
        || (is_unassigning
          ? 'Template form unassigned from onboarding form successfully.'
          : 'Template form assigned successfully.'),
      );
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to update onboarding form assignment.'),
      );
    } finally {
      await this.close_loader(loader);
      this.is_submitting_assignment = false;
      this.assignment_target_onboarding_form_id = '';
    }
  },
};

export default template_form_management_assignment_methods;
